/** Registration step submitted to pre-validate a session before payment. */
export const PAYMENT_PRE_SUBMIT_STEP = 'payment_pre_submit'

export interface PaymentGateResult {
  /** True only when the server accepted the pre-submit check. */
  readonly ok: boolean
  /** User-facing copy for the failure. Empty when `ok`. */
  readonly message: string
  /**
   * Whether the failure invalidates the whole flow. When true the caller
   * should delete the local session and restart rather than let the visitor
   * retry against state the server has moved past.
   */
  readonly shouldResetSession: boolean
}

export const PAYMENT_GATE_OK: PaymentGateResult = Object.freeze({
  ok: true,
  message: '',
  shouldResetSession: false,
})

/** Shown when the API rejects the pre-submit without a usable message. */
export const PAYMENT_GATE_FALLBACK_MESSAGE =
  'We could not verify your registration session. Please try again.'

/**
 * A session the server no longer recognizes. `410 Gone` is the flow's existing
 * expiry signal (see the payment step's session-expired handling); `404` means
 * it was deleted outright. Either way there is nothing left to retry against.
 */
const SESSION_GONE_STATUSES: ReadonlySet<number> = new Set([404, 410])

/**
 * Maps a failed pre-submit request to a gate result.
 *
 * Any rejection blocks the payment — confirming a charge we could not validate
 * is exactly the risk this gate exists to remove. The distinction that matters
 * is whether the registrant can retry (a transient/validation error) or the
 * session is gone and the flow must restart.
 *
 * @param error - Error thrown by `req`, carrying `status`.
 * @param message - Resolved API message; falls back to generic copy when empty.
 */
export const paymentGateFromRequestError = (
  error: { status?: number } | null | undefined,
  message?: string | null
): PaymentGateResult =>
  Object.freeze({
    ok: false,
    message: message || PAYMENT_GATE_FALLBACK_MESSAGE,
    shouldResetSession: SESSION_GONE_STATUSES.has(error?.status as number),
  })
